/**
 * Bonded design-system primitives, ported to inline styles for the funnel.
 * Values mirror bonded-design-system/_ds_bundle.js + tokens/*.css exactly:
 * 3px ink outline, pill radius, hard "sticker" shadow (no blur), bounce motion,
 * hover lifts up-left, active pushes down-right.
 */
import { useState, type CSSProperties, type InputHTMLAttributes, type ReactNode } from 'react';

function useSticker() {
  const [hover, setHover] = useState(false);
  const [press, setPress] = useState(false);
  const handlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => {
      setHover(false);
      setPress(false);
    },
    onMouseDown: () => setPress(true),
    onMouseUp: () => setPress(false),
    onTouchStart: () => setPress(true),
    onTouchEnd: () => setPress(false),
  };
  return { hover, press, handlers };
}

// ---- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'accent' | 'sunshine' | 'cloud';
type ButtonSize = 'sm' | 'md' | 'lg';

const BTN_BG: Record<ButtonVariant, string> = {
  primary: 'var(--action-primary)',
  accent: 'var(--action-accent)',
  sunshine: 'var(--sunshine)',
  cloud: 'var(--cloud)',
};
const BTN_FG: Record<ButtonVariant, string> = {
  primary: 'var(--action-primary-label)',
  accent: 'var(--action-accent-label)',
  sunshine: 'var(--ink)',
  cloud: 'var(--ink)',
};
const BTN_PAD: Record<ButtonSize, string> = { sm: '10px 18px', md: '14px 26px', lg: '18px 34px' };
const BTN_FONT: Record<ButtonSize, string> = { sm: '15px', md: '17px', lg: '20px' };

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  disabled,
  loading,
  onClick,
  children,
  style,
  type = 'button',
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  children: ReactNode;
  style?: CSSProperties;
  type?: 'button' | 'submit';
}) {
  const { hover, press, handlers } = useSticker();
  const isDisabled = disabled || loading;
  const shadow = press ? 'none' : hover ? 'var(--shadow-sticker-lg)' : 'var(--shadow-sticker)';
  const transform = press ? 'translate(4px,4px)' : hover ? 'translate(-2px,-2px)' : 'none';
  return (
    <button
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      {...(isDisabled ? {} : handlers)}
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: BTN_FONT[size],
        lineHeight: 1,
        padding: BTN_PAD[size],
        width: block ? '100%' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: BTN_FG[variant],
        background: BTN_BG[variant],
        border: '3px solid var(--ink)',
        borderRadius: 'var(--radius-pill)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.45 : 1,
        boxShadow: isDisabled ? 'var(--shadow-sticker)' : shadow,
        transform: isDisabled ? 'none' : transform,
        transition:
          'transform var(--dur-quick) var(--ease-bounce), box-shadow var(--dur-quick) var(--ease-bounce)',
        ...style,
      }}
    >
      {loading ? '…' : children}
    </button>
  );
}

// ---- OptionChip (quiz answer) ----------------------------------------------

export function OptionChip({
  label,
  hint,
  selected,
  multi,
  disabled,
  onClick,
}: {
  label: string;
  hint?: string;
  selected?: boolean;
  multi?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const { hover, handlers } = useSticker();
  const active = selected || false;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      {...(disabled ? {} : handlers)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        minHeight: 58,
        padding: '13px 18px',
        textAlign: 'left',
        fontFamily: 'var(--font-ui)',
        fontWeight: 800,
        fontSize: '1rem',
        color: 'var(--ink)',
        background: active ? 'var(--sunshine)' : 'var(--cloud)',
        border: '3px solid var(--ink)',
        borderRadius: 'var(--radius-md)',
        boxShadow: active ? 'var(--shadow-hover)' : hover ? 'var(--shadow-hover)' : 'var(--shadow-sticker)',
        transform: active || hover ? 'translate(1px,1px)' : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !active ? 0.6 : 1,
        transition: 'transform var(--dur-quick) var(--ease-soft), background var(--dur-quick) var(--ease-soft)',
      }}
    >
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          display: 'inline-grid',
          placeItems: 'center',
          width: 24,
          height: 24,
          borderRadius: multi ? 6 : '50%',
          border: '3px solid var(--ink)',
          background: active ? 'var(--bubblegum-deep)' : 'var(--cloud)',
          boxShadow: active && !multi ? 'inset 0 0 0 4px var(--cloud)' : undefined,
          fontSize: '.8rem',
          fontWeight: 900,
          color: 'var(--ink)',
        }}
      >
        {active && multi ? '✓' : ''}
      </span>
      <span style={{ flex: 1 }}>
        {label}
        {hint ? (
          <span style={{ display: 'block', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '.8rem', color: 'var(--text-muted)' }}>
            {hint}
          </span>
        ) : null}
      </span>
    </button>
  );
}

// ---- OptionTile (visual grid choice) ---------------------------------------

/**
 * The large, tappable tile used where the choice should read visually before it
 * reads as text — the landing question and the age gate. Two per row on mobile.
 */
export function OptionTile({
  label,
  emoji,
  selected,
  quiet,
  onClick,
}: {
  label: string;
  emoji?: string;
  selected?: boolean;
  /** De-emphasised styling for opt-out choices like "I'm under 18". */
  quiet?: boolean;
  onClick: () => void;
}) {
  const { hover, press, handlers } = useSticker();
  return (
    <button
      onClick={onClick}
      aria-pressed={selected || false}
      {...handlers}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: quiet ? 64 : 116,
        padding: '16px 12px',
        textAlign: 'center',
        fontFamily: 'var(--font-ui)',
        fontWeight: 800,
        fontSize: quiet ? '.95rem' : '1.05rem',
        color: 'var(--ink)',
        background: selected ? 'var(--sunshine)' : quiet ? 'var(--paper)' : 'var(--cloud)',
        border: '3px solid var(--ink)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: press ? 'none' : hover || selected ? 'var(--shadow-sticker-lg)' : 'var(--shadow-sticker)',
        transform: press ? 'translate(4px,4px)' : hover ? 'translate(-2px,-2px)' : 'none',
        cursor: 'pointer',
        transition: 'transform var(--dur-quick) var(--ease-bounce), box-shadow var(--dur-quick) var(--ease-bounce)',
      }}
    >
      {emoji && !quiet ? (
        <span aria-hidden style={{ fontSize: '2rem', lineHeight: 1 }}>
          {emoji}
        </span>
      ) : null}
      <span>{label}</span>
    </button>
  );
}

// ---- Checkbox ---------------------------------------------------------------

/**
 * Used for the optional research consent on the landing screen. Deliberately a
 * real checkbox, unticked by default and separate from the Terms line — consent
 * to non-essential processing has to be freely given and separable.
 */
export function Checkbox({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-body)',
        fontSize: '.85rem',
        color: 'var(--text-muted)',
        lineHeight: 1.45,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        style={{
          flex: '0 0 auto',
          width: 20,
          height: 20,
          marginTop: 1,
          accentColor: 'var(--bubblegum-deep)',
          cursor: 'pointer',
        }}
      />
      <span>
        <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{label}</span>
        {hint ? <span style={{ display: 'block' }}>{hint}</span> : null}
      </span>
    </label>
  );
}

// ---- Card -------------------------------------------------------------------

export function Card({
  children,
  accent = 'var(--cloud)',
  padded = true,
  style,
}: {
  children: ReactNode;
  accent?: string;
  padded?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        background: accent,
        border: '3px solid var(--ink)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-sticker)',
        padding: padded ? 'var(--space-5)' : 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ---- ProgressBar ------------------------------------------------------------

export function ProgressBar({ value }: { value: number }) {
  return (
    <div
      style={{
        width: 'min(88%,680px)',
        height: 9,
        margin: '0 auto',
        overflow: 'hidden',
        border: '2px solid var(--ink)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--cloud)',
      }}
    >
      <span
        style={{
          display: 'block',
          height: '100%',
          width: `${Math.max(0, Math.min(100, value))}%`,
          background: 'var(--bubblegum-deep)',
          transition: 'width var(--dur-normal) var(--ease-soft)',
        }}
      />
    </div>
  );
}

// ---- Badge ------------------------------------------------------------------

export function Badge({
  children,
  color = 'var(--sunshine)',
  rotate = -3,
}: {
  children: ReactNode;
  color?: string;
  rotate?: number;
}) {
  return (
    <span
      style={{
        display: 'inline-block',
        background: color,
        color: 'var(--ink)',
        border: '3px solid var(--ink)',
        borderRadius: 'var(--radius-pill)',
        padding: '8px 18px',
        fontFamily: 'var(--font-display)',
        fontWeight: 700,
        fontSize: '.9rem',
        transform: `rotate(${rotate}deg)`,
        boxShadow: 'var(--shadow-hover)',
      }}
    >
      {children}
    </span>
  );
}

// ---- Input ------------------------------------------------------------------

export function Input({
  label,
  ...props
}: { label?: string } & InputHTMLAttributes<HTMLInputElement>) {
  const [focus, setFocus] = useState(false);
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label ? (
        <span
          style={{
            fontFamily: 'var(--font-ui)',
            fontWeight: 800,
            fontSize: '.8rem',
            letterSpacing: '.08em',
            textTransform: 'uppercase',
            color: 'var(--ink)',
          }}
        >
          {label}
        </span>
      ) : null}
      <input
        {...props}
        onFocus={(e) => {
          setFocus(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocus(false);
          props.onBlur?.(e);
        }}
        style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 600,
          fontSize: '1rem',
          color: 'var(--ink)',
          background: 'var(--cloud)',
          border: '3px solid var(--ink)',
          borderRadius: 'var(--radius-pill)',
          padding: '14px 22px',
          outline: 'none',
          minHeight: 54,
          boxShadow: focus ? '0 0 0 4px var(--focus-ring)' : 'var(--shadow-sticker)',
          transition: 'box-shadow var(--dur-quick) var(--ease-soft)',
        }}
      />
    </label>
  );
}
